import { useQuery } from '@cbgms/base/http/use-query'
import { DispatchReturnValue } from '@cbgms/base/redux/async-action'
import { AnyAction } from 'redux'
import { useDispatch } from 'react-redux'
import { IShop } from '@cbgms/api'
import { getCompanyShops } from 'app/shops/state/actions'

const COMPANY_SHOPS_QUERY_KEY = ['CompanyShops'] as const
type AsyncDispatch = <T extends AnyAction>(action: T) => DispatchReturnValue<T>

/**
 * Shared hook for fetching all company shops with deduplication.
 *
 * This prevents duplicate GET /modules/shops calls when multiple components
 * mount (e.g. TopBar remounts on shop change due to ShopChangeRefreshProvider key={shopID}).
 *
 * The company shops list is NOT shop-specific — it is the same across all shops
 * in a company and must NOT be invalidated on shop change.
 */
export const useCompanyShops = () => {
  const dispatch = useDispatch<AsyncDispatch>()

  return useQuery<IShop[], Error>(
    COMPANY_SHOPS_QUERY_KEY,
    async (): Promise<IShop[]> => {
      const result = (await dispatch(getCompanyShops())) as unknown as { Data?: IShop[] }
      return result.Data ?? []
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes — prevents refetch on remount (e.g. shop change)
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false
    }
  )
}
